import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { organizationsApi } from '../api/organizations';
import { usersApi } from '../api/users';
import DashboardLayout from '../components/DashboardLayout';
import ImageUploadModal from '../components/ImageUploadModal';
import './DashboardPages.css';

function DashboardAdminOrganizationEdit() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const [error, setError] = useState('');
	const [showSuccess, setShowSuccess] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [formData, setFormData] = useState({
		name: '',
		addressOne: '',
		addressTwo: '',
		city: '',
		state: '',
		zipCode: '',
		phoneNumber: '',
		website: '',
		facebook: '',
		x: '',
		instagram: '',
		primaryColor: '#3388B3',
		secondaryColor: '#666563',
		accentColor: '#9D94E8',
		orgPicture: null,
		coverImage: null,
		seniorPastorId: null,
		adminUserIds: [],
	});
	const [isImageModalOpen, setIsImageModalOpen] = useState(false);
	const [activeImageEdit, setActiveImageEdit] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const [selectedAdmins, setSelectedAdmins] = useState([]);

	useEffect(() => {
		if (location.state?.organization) {
			const org = location.state.organization;
			setFormData({
				name: org.name || '',
				addressOne: org.addressOne || '',
				addressTwo: org.addressTwo || '',
				city: org.city || '',
				state: org.state || '',
				zipCode: org.zipCode || '',
				phoneNumber: org.phoneNumber || '',
				website: org.website || '',
				facebook: org.facebook || '',
				x: org.x || '',
				instagram: org.instagram || '',
				primaryColor: org.primaryColor || '#3388B3',
				secondaryColor: org.secondaryColor || '#666563',
				accentColor: org.accentColor || '#9D94E8',
				orgPicture: org.orgPicture || null,
				coverImage: org.coverImage || null,
				seniorPastorId: org.seniorPastorId || null,
				adminUserIds: org.adminUserIds || [],
			});
			if (org.adminUserIds) {
				setSelectedAdmins(org.adminUserIds);
			}
			setIsLoading(false);
		} else {
			fetchOrganization();
		}
	}, [id, location.state]);

	const fetchOrganization = async () => {
		try {
			setIsLoading(true);
			const org = await organizationsApi.getById(id);

			setFormData({
				name: org.name || '',
				addressOne: org.addressOne || '',
				addressTwo: org.addressTwo || '',
				city: org.city || '',
				state: org.state || '',
				zipCode: org.zipCode || '',
				phoneNumber: org.phoneNumber || '',
				website: org.website || '',
				facebook: org.facebook || '',
				x: org.x || '',
				instagram: org.instagram || '',
				primaryColor: org.primaryColor || '#3388B3',
				secondaryColor: org.secondaryColor || '#666563',
				accentColor: org.accentColor || '#9D94E8',
				orgPicture: org.orgPicture || null,
				coverImage: org.coverImage || null,
				seniorPastorId: org.seniorPastorId || null,
				adminUserIds: org.adminUserIds || [],
			});
		} catch (err) {
			setError('Failed to fetch organization');
			console.error('Error details:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleUserSearch = async (email) => {
		try {
			const data = await usersApi.usersByEmail.search(email);
			const filteredResults = data.users.filter(
				(user) => !selectedAdmins.some((admin) => admin.id === user.id)
			);
			setSearchResults(filteredResults);
		} catch (error) {
			console.error('Error searching users:', error);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			setError('');
			setShowSuccess(false);

			// Create submission data from formData and selectedAdmins
			const dataToSubmit = {
				...formData,
				adminUserIds: selectedAdmins.map((admin) => admin.id),
			};

			await organizationsApi.update(id, dataToSubmit);

			setShowSuccess(true);
			setTimeout(() => {
				setShowSuccess(false);
			}, 3000);
		} catch (err) {
			setError('Failed to update organization: ' + err.message);
			console.error(err);
		}
	};

	const handleImageSave = (imageData) => {
		setFormData({
			...formData,
			[activeImageEdit]: imageData,
		});
	};

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className='dashboard-page'>
					<div className='content-section'>
						<p>Loading organization data...</p>
					</div>
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<div className='page-header'>
					<button
						className='back-button'
						onClick={() =>
							navigate('/dashboard/admin/organizations')
						}>
						← Back to Organizations
					</button>
					<h1>Edit Organization</h1>
				</div>

				<div className='content-section'>
					{error && <div className='error-message'>{error}</div>}

					<div
						className='org-cover-container'
						onClick={() => {
							setActiveImageEdit('coverImage');
							setIsImageModalOpen(true);
						}}>
						{formData.coverImage ? (
							<img
								src={
									formData.coverImage instanceof File
										? URL.createObjectURL(
												formData.coverImage
										  )
										: formData.coverImage.startsWith('@')
										? formData.coverImage.substring(1)
										: formData.coverImage
								}
								alt='Organization Cover'
								className='org-cover-image'
							/>
						) : (
							<div className='org-cover-placeholder'>
								<span className='material-icons'>image</span>
								<span>Click to add cover image</span>
							</div>
						)}

						<div
							className='org-avatar-container'
							onClick={(e) => {
								e.stopPropagation();
								setActiveImageEdit('orgPicture');
								setIsImageModalOpen(true);
							}}>
							{formData.orgPicture ? (
								<img
									src={
										formData.orgPicture instanceof File
											? URL.createObjectURL(
													formData.orgPicture
											  )
											: formData.orgPicture
									}
									alt='Organization'
									className='org-avatar'
								/>
							) : (
								<div className='org-avatar-placeholder'>
									<span className='material-icons'>
										business
									</span>
								</div>
							)}
						</div>
					</div>

					<ImageUploadModal
						isOpen={isImageModalOpen}
						onClose={() => setIsImageModalOpen(false)}
						currentImage={formData[activeImageEdit]}
						onSave={handleImageSave}
						shouldCrop={false}
					/>

					<form
						className='org-form'
						onSubmit={handleSubmit}>
						<div className='form-group'>
							<label>Organization Name</label>
							<input
								type='text'
								placeholder='Enter organization name'
								className='form-input'
								value={formData.name}
								onChange={(e) =>
									setFormData({
										...formData,
										name: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Organization Admins</label>
							<div className='admin-search-container'>
								{/* Hidden input to fool the browser's autofill */}
								<input
									type='password'
									style={{ display: 'none' }}
									autoComplete='new-password'
								/>
								<input
									type='text'
									placeholder='Search users by email'
									className='form-input'
									value={searchTerm}
									onChange={(e) => {
										setSearchTerm(e.target.value);
										if (e.target.value.length >= 3) {
											handleUserSearch(e.target.value);
										} else {
											setSearchResults([]);
										}
									}}
									autoComplete='new-password'
									autoCorrect='off'
									autoCapitalize='off'
									spellCheck='false'
									role='combobox'
									aria-autocomplete='list'
								/>
								{searchResults.length > 0 && (
									<div className='search-results'>
										{searchResults.map((user) => (
											<div
												key={user.id}
												className='search-result-item'
												onClick={() => {
													setSelectedAdmins([
														...selectedAdmins,
														user,
													]);
													setSearchTerm('');
													setSearchResults([]);
												}}>
												{user.firstName} {user.lastName}{' '}
												- {user.email}
											</div>
										))}
									</div>
								)}
							</div>
							{selectedAdmins.length > 0 && (
								<div className='selected-admins'>
									{selectedAdmins.map((admin) => (
										<div
											key={admin.id}
											className='selected-admin'>
											{admin.firstName} {admin.lastName}
											<button
												type='button'
												className='clear-admin'
												onClick={() => {
													setSelectedAdmins(
														selectedAdmins.filter(
															(a) =>
																a.id !==
																admin.id
														)
													);
												}}>
												×
											</button>
										</div>
									))}
								</div>
							)}
						</div>

						<div className='form-group'>
							<label>Address Line 1</label>
							<input
								type='text'
								placeholder='Street address'
								className='form-input'
								value={formData.addressOne}
								onChange={(e) =>
									setFormData({
										...formData,
										addressOne: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Address Line 2</label>
							<input
								type='text'
								placeholder='Apt, Suite, etc. (optional)'
								className='form-input'
								value={formData.addressTwo}
								onChange={(e) =>
									setFormData({
										...formData,
										addressTwo: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-row'>
							<div className='form-group'>
								<label>City</label>
								<input
									type='text'
									placeholder='City'
									className='form-input'
									value={formData.city}
									onChange={(e) =>
										setFormData({
											...formData,
											city: e.target.value,
										})
									}
								/>
							</div>
							<div className='form-group'>
								<label>State</label>
								<input
									type='text'
									placeholder='State'
									className='form-input'
									value={formData.state}
									onChange={(e) =>
										setFormData({
											...formData,
											state: e.target.value,
										})
									}
								/>
							</div>
							<div className='form-group'>
								<label>ZIP Code</label>
								<input
									type='text'
									placeholder='ZIP Code'
									className='form-input'
									value={formData.zipCode}
									onChange={(e) =>
										setFormData({
											...formData,
											zipCode: e.target.value,
										})
									}
								/>
							</div>
						</div>

						<div className='form-group'>
							<label>Phone Number</label>
							<input
								type='tel'
								placeholder='Phone number'
								className='form-input'
								value={formData.phoneNumber}
								onChange={(e) =>
									setFormData({
										...formData,
										phoneNumber: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Website</label>
							<input
								type='url'
								placeholder='Website URL'
								className='form-input'
								value={formData.website}
								onChange={(e) =>
									setFormData({
										...formData,
										website: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Facebook</label>
							<input
								type='url'
								placeholder='Facebook URL (optional)'
								className='form-input'
								value={formData.facebook}
								onChange={(e) =>
									setFormData({
										...formData,
										facebook: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>X (Twitter)</label>
							<input
								type='url'
								placeholder='X profile URL (optional)'
								className='form-input'
								value={formData.x}
								onChange={(e) =>
									setFormData({
										...formData,
										x: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Instagram</label>
							<input
								type='url'
								placeholder='Instagram URL (optional)'
								className='form-input'
								value={formData.instagram}
								onChange={(e) =>
									setFormData({
										...formData,
										instagram: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Primary Color</label>
							<input
								type='color'
								className='form-input color-input'
								value={formData.primaryColor}
								onChange={(e) =>
									setFormData({
										...formData,
										primaryColor: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Secondary Color</label>
							<input
								type='color'
								className='form-input color-input'
								value={formData.secondaryColor}
								onChange={(e) =>
									setFormData({
										...formData,
										secondaryColor: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Accent Color</label>
							<input
								type='color'
								className='form-input color-input'
								value={formData.accentColor}
								onChange={(e) =>
									setFormData({
										...formData,
										accentColor: e.target.value,
									})
								}
							/>
						</div>

						<div className='submit-container'>
							<button
								type='submit'
								className='submit-btn'>
								Save Changes
							</button>
							{showSuccess && (
								<span className='success-checkmark'>
									<span className='material-icons'>
										check_circle
									</span>
								</span>
							)}
						</div>
					</form>
				</div>
			</div>
		</DashboardLayout>
	);
}

export default DashboardAdminOrganizationEdit;
